/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/entryClipboard.ts
 * FILE_ID: UI-KANBAN-ENTRIES-CLIP-001
 * PURPOSE: Excel ↔ Entries bridge — parse a TSV clipboard paste into
 *          draft rows, and serialise live entries to CSV for export.
 * WHY: ARC-3 §4.3 (page-3 client note): "Paste-from-Excel into the
 *      sub-entry table (TSV clipboard → rows). Export table to CSV."
 * FLOW:
 *   clipboard text → parseTsvToDrafts → EntryDraftRow[]   (paste)
 *   Card[]         → entriesToCsv    → RFC-4180 CSV string (export)
 * RELATED:
 *   - entryDraft.ts: the draft row model this fills
 *   - CardEntriesSection.tsx: onPaste handler + Export button
 */

import type { KanbanMember } from '../../../api'
import type { Card } from '../../../types'
import { csvCell } from '../../../utils/csv'
import { emptyDraftRow, type EntryDraftRow } from './entryDraft'

/** True when pasted text spans multiple cells — the signal to expand
 *  the paste into table rows instead of letting the focused input take
 *  the raw string. */
export function isTabularPaste(text: string): boolean {
  const trimmed = text.replace(/\r?\n$/, '')
  return trimmed.includes('\t') || trimmed.includes('\n')
}

/** "12/08" or "12/08/2026" (AU day-first, matching the client's sheets)
 *  or an already-ISO "2026-08-12" → ISO `YYYY-MM-DD`; anything else ''. */
export function normalisePastedDate(raw: string): string {
  const text = raw.trim()
  if (!text) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text
  const m = text.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/)
  if (!m) return ''
  const day = Number(m[1])
  const month = Number(m[2])
  let year = m[3] ? Number(m[3]) : new Date().getFullYear()
  if (year < 100) year += 2000
  if (day < 1 || day > 31 || month < 1 || month > 12) return ''
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Excel TSV → draft rows, columns in table order:
 *  teammate | action | what | date | amount. The first cell is treated
 *  as the teammate NAME when it matches an org member
 *  (case-insensitive); otherwise the columns shift left one slot so the
 *  first cell becomes `action` and nothing is lost. Shorter pastes fill
 *  from the left; blank lines are skipped. */
export function parseTsvToDrafts(
  text: string,
  members: KanbanMember[],
): EntryDraftRow[] {
  const memberByName = new Map(
    members.map((m) => [m.name.trim().toLowerCase(), m.id]),
  )
  const rows: EntryDraftRow[] = []
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue
    const cells = line.split('\t').map((c) => c.trim())
    const draft = emptyDraftRow()

    let rest = cells
    const teammateId = memberByName.get((cells[0] ?? '').toLowerCase())
    if (teammateId != null) {
      draft.teammate_id = teammateId
      rest = cells.slice(1)
    }
    draft.action = rest[0] ?? ''
    draft.what = rest[1] ?? ''
    draft.date = normalisePastedDate(rest[2] ?? '')
    draft.amountText = rest[3] ?? ''
    rows.push(draft)
  }
  return rows
}

// csvCell moved to utils/csv.ts (UI-KANBAN-UTIL-CSV-001) so the board
// Table view export shares the exact same quoting — behaviour identical.

/** Live entry sub-cards → CSV (header + one line per entry). Amount is
 *  exported in dollars so the sheet round-trips with the paste path. */
export function entriesToCsv(
  entries: Card[],
  memberById: Map<number, KanbanMember>,
): string {
  const lines = ['teammate,action,date,amount,status']
  for (const e of entries) {
    const teammate = (e.assigned_user_ids ?? [])
      .map((id) => memberById.get(id)?.name)
      .filter(Boolean)
      .join('; ')
    const amount =
      e.amount_cents != null ? (e.amount_cents / 100).toFixed(2) : ''
    lines.push([
      csvCell(teammate),
      csvCell(e.title ?? ''),
      e.action_date ?? '',
      amount,
      e.status ?? '',
    ].join(','))
  }
  return lines.join('\n')
}
